import React, { useCallback, useMemo, useState } from "react";
import classnames from "classnames";
import TextareaAutosize from "react-textarea-autosize";

import "./CommentInput.styles.scss";
import { Avatar } from "components";
import { CommentAudience } from "components/popups";
import {
  useDealFormSectionIdSelect,
  useUserSelector,
  useDealFormIdSelect,
  useUserRoleSelector,
  useCommentsActions,
  useViewActions
} from "store";
import { useDealParamsHooks, useCommentsUrlHooks, useFormatDateHook } from "hooks";
import { IRole, KeyOfAudiences, ICommentChild, ICommentParent } from "store/types";
import { isRoleAnalyst } from "utils/user";
import { Button } from "components/forms";

export interface CommentInputProps {
  onCancel: () => void;
  parentCommentId?: number;
  question_id?: number | null;
  parentAudience?: KeyOfAudiences;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  onCancel,
  parentCommentId,
  question_id,
  parentAudience
}) => {
  const { createCommentAction } = useCommentsActions();
  const { commentsScrollAction } = useViewActions();
  const { setCommentToUrl } = useCommentsUrlHooks();
  const dateFormat = useFormatDateHook();

  const { dealId } = useDealParamsHooks();
  const user = useUserSelector();
  const sectionId = useDealFormSectionIdSelect();
  const formId = useDealFormIdSelect();
  const role: IRole | null = useUserRoleSelector();

  const [inputValue, setInputValue] = useState<string>("");
  const [inputLoading, setInputLoading] = useState<boolean>(false);

  const [isAudienceOpen, setIsAudienceOpen] = useState<boolean>(false);

  const defaultAudience: KeyOfAudiences = useMemo(
    () => (isRoleAnalyst({ role }) ? "everyone" : "tenants_team"),
    [role]
  );

  const [audience, setAudience] = useState<KeyOfAudiences>(parentAudience ?? defaultAudience);

  const sendComment = useCallback(async () => {
    try {
      if (sectionId && formId) {
        setInputLoading(true);
        const payload: ICommentParent | ICommentChild = await createCommentAction(dealId, {
          text: inputValue,
          form_id: formId,
          parent_id: parentCommentId,
          question_id,
          section_id: sectionId,
          audience
        });
        setInputValue("");
        setInputLoading(false);
        onCancel();
        setCommentToUrl(payload);
        commentsScrollAction(true);
      }
    } catch (error) {
      console.log(error);
      setInputLoading(false);
    }
  }, [inputValue, formId, parentCommentId, question_id, sectionId, dealId, audience]);

  const commentInputClasses = classnames("comment-input", "opacity-appearance", {
    "comment-input--reply": parentCommentId
  });

  return (
    <div className={commentInputClasses}>
      <div className="author flex-row-fs-fc">
        <Avatar size={parentCommentId ? 30 : 38} className="author-image" src={user.profile_photo_url} />
        <div className="flex-row-fc-fc">
          <div className="flex-col-fs-fs">
            <span className="author-name">{`${user.first_name} ${user.last_name}`}</span>
            <span className="date-row">{dateFormat({ date: new Date().toISOString(), format: "LT" })}</span>
          </div>
          {!parentCommentId && (
            <CommentAudience
              isOpen={isAudienceOpen}
              setIsOpen={setIsAudienceOpen}
              onItemClick={setAudience}
              selectedAudience={audience}
            />
          )}
        </div>
      </div>
      <div
        className={classnames("comment-spacer-wrap", {
          "comment-spacer-wrap--nested": parentCommentId
        })}
      >
        <TextareaAutosize
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          className="comment-input__textarea no-outline"
        />
        <div className="flex-row-sb-fc">
          <Button className="comment-input__button" variant="light-border" title="Cancel" onClick={onCancel} />
          <Button
            className="comment-input__button"
            isLoading={inputLoading}
            disabled={!inputValue}
            title="Post"
            onClick={sendComment}
          />
        </div>
      </div>
    </div>
  );
};
